import { useCallback, useEffect, useState } from "react";

/**
 * Hook de gestion du mode sombre pour les dashboards.
 * - Lecture/écriture dans localStorage sous la clé `dashboard-dark-mode`.
 * - Initialise depuis la préférence système si rien n'est stocké.
 * - Synchronise les onglets via l'event `storage`.
 */
const STORAGE_KEY = "dashboard-dark-mode";

function readInitial(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "1") return true;
    if (stored === "0") return false;
    // Défaut : mode CLAIR (on ignore volontairement prefers-color-scheme
    // pour que les dashboards soient en blanc par défaut, l'utilisateur
    // pouvant basculer en sombre via le bouton de l'interface).
    return false;
  } catch {
    return false;
  }
}

export function useDarkMode(): {
  isDark: boolean;
  toggle: () => void;
  setDark: (v: boolean) => void;
} {
  const [isDark, setIsDark] = useState<boolean>(readInitial);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, isDark ? "1" : "0");
    } catch {
      /* noop */
    }
  }, [isDark]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setIsDark(e.newValue === "1");
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const toggle = useCallback(() => setIsDark((v) => !v), []);
  const setDark = useCallback((v: boolean) => setIsDark(v), []);

  return { isDark, toggle, setDark };
}
